export interface OnboardingStep {
  id: string;
  title: string;
  description: string;
  action?: string;
  route?: string;
  icon: string;
  category: 'essential' | 'recommended' | 'advanced';
  completed: boolean;
  order: number;
}

export interface OnboardingProgress {
  steps: OnboardingStep[];
  isCompleted: boolean;
  completedSteps: number;
  totalSteps: number;
  showOnboarding: boolean;
  currentStep?: string;
}

const STORAGE_KEY = 'managetix_onboarding';

export const defaultOnboardingSteps: OnboardingStep[] = [
  {
    id: 'create_first_event',
    title: 'Create Your First Event',
    description: 'Add an event to start managing tickets and sales',
    action: 'Create Event',
    route: '/events',
    icon: 'calendar-outline',
    category: 'essential',
    completed: false,
    order: 1,
  },
  {
    id: 'add_inventory',
    title: 'Add Ticket Inventory',
    description: 'Add your ticket inventory for the event',
    action: 'Add Tickets',
    route: '/events',
    icon: 'ticket-outline',
    category: 'essential',
    completed: false,
    order: 2,
  },
  {
    id: 'record_first_sale',
    title: 'Record Your First Sale',
    description: 'Log a ticket sale to track your revenue',
    action: 'Add Sale',
    route: '/sales',
    icon: 'cart-outline',
    category: 'essential',
    completed: false,
    order: 3,
  },
  {
    id: 'setup_supplier',
    title: 'Add a Supplier',
    description: 'Track your suppliers and manage payments',
    action: 'Add Supplier',
    route: '/suppliers',
    icon: 'business-outline',
    category: 'recommended',
    completed: false,
    order: 4,
  },
  {
    id: 'import_data',
    title: 'Import External Data',
    description: 'Import sales from StubHub, LiveFootballTickets, etc.',
    action: 'Import Data',
    route: '/import',
    icon: 'cloud-upload-outline',
    category: 'recommended',
    completed: false,
    order: 5,
  },
  {
    id: 'view_reports',
    title: 'Explore Reports',
    description: 'Analyze your performance with detailed reports',
    action: 'View Reports',
    route: '/reports',
    icon: 'stats-chart-outline',
    category: 'recommended',
    completed: false,
    order: 6,
  },
  {
    id: 'export_data',
    title: 'Export Your Data',
    description: 'Export your data for backup or analysis',
    action: 'Export Data',
    route: '/export',
    icon: 'cloud-download-outline',
    category: 'advanced',
    completed: false,
    order: 7,
  },
];

export class OnboardingManager {
  private static instance: OnboardingManager;
  private progress: OnboardingProgress;

  private constructor() {
    this.progress = this.loadProgress();
  }

  public static getInstance(): OnboardingManager {
    if (!OnboardingManager.instance) {
      OnboardingManager.instance = new OnboardingManager();
    }
    return OnboardingManager.instance;
  }

  private loadProgress(): OnboardingProgress {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Merge with default steps to handle new steps
        const mergedSteps = this.mergeSteps(defaultOnboardingSteps, parsed.steps || []);
        return {
          ...parsed,
          steps: mergedSteps,
          completedSteps: mergedSteps.filter(s => s.completed).length,
          totalSteps: mergedSteps.length,
        };
      }
    } catch (error) {
      console.warn('Failed to load onboarding progress:', error);
    }

    return {
      steps: [...defaultOnboardingSteps],
      isCompleted: false,
      completedSteps: 0,
      totalSteps: defaultOnboardingSteps.length,
      showOnboarding: true,
    };
  }

  private mergeSteps(defaultSteps: OnboardingStep[], storedSteps: OnboardingStep[]): OnboardingStep[] {
    const mergedSteps = [...defaultSteps];
    
    // Update completion status from stored steps
    storedSteps.forEach(storedStep => {
      const index = mergedSteps.findIndex(step => step.id === storedStep.id);
      if (index !== -1) {
        mergedSteps[index].completed = storedStep.completed;
      }
    });

    return mergedSteps.sort((a, b) => a.order - b.order);
  }

  private saveProgress(): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.progress));
    } catch (error) {
      console.warn('Failed to save onboarding progress:', error);
    }
  }

  public getProgress(): OnboardingProgress {
    return { ...this.progress };
  }

  public completeStep(stepId: string): void {
    const step = this.progress.steps.find(s => s.id === stepId);
    if (step && !step.completed) {
      step.completed = true;
      this.progress.completedSteps++;
      this.progress.isCompleted = this.progress.completedSteps === this.progress.totalSteps;
      this.saveProgress();
    }
  }

  public resetStep(stepId: string): void {
    const step = this.progress.steps.find(s => s.id === stepId);
    if (step && step.completed) {
      step.completed = false;
      this.progress.completedSteps--;
      this.progress.isCompleted = false;
      this.saveProgress();
    }
  }

  public dismissOnboarding(): void {
    this.progress.showOnboarding = false;
    this.saveProgress();
  }

  public showOnboarding(): void {
    this.progress.showOnboarding = true;
    this.saveProgress();
  }

  public resetOnboarding(): void {
    this.progress = {
      steps: [...defaultOnboardingSteps],
      isCompleted: false,
      completedSteps: 0,
      totalSteps: defaultOnboardingSteps.length,
      showOnboarding: true,
    };
    this.saveProgress();
  }

  public getEssentialSteps(): OnboardingStep[] {
    return this.progress.steps.filter(step => step.category === 'essential');
  }

  public getRecommendedSteps(): OnboardingStep[] {
    return this.progress.steps.filter(step => step.category === 'recommended');
  }

  public getAdvancedSteps(): OnboardingStep[] {
    return this.progress.steps.filter(step => step.category === 'advanced');
  }

  public getNextStep(): OnboardingStep | undefined {
    return this.progress.steps.find(step => !step.completed);
  }

  public getCompletionPercentage(): number {
    if (this.progress.totalSteps === 0) return 0;
    return Math.round((this.progress.completedSteps / this.progress.totalSteps) * 100);
  }

  public shouldShowOnboarding(): boolean {
    return this.progress.showOnboarding && !this.progress.isCompleted;
  }

  // Auto-detection methods for completing steps
  public checkEventCreated(): void {
    // This would be called when an event is created
    this.completeStep('create_first_event');
  }

  public checkInventoryAdded(): void {
    // This would be called when inventory is added
    this.completeStep('add_inventory');
  }

  public checkSaleRecorded(): void {
    // This would be called when a sale is recorded
    this.completeStep('record_first_sale');
  }

  public checkSupplierAdded(): void {
    // This would be called when a supplier is added
    this.completeStep('setup_supplier');
  }

  public checkDataImported(): void {
    // This would be called when data is imported
    this.completeStep('import_data');
  }

  public checkReportsViewed(): void {
    // This would be called when reports are viewed
    this.completeStep('view_reports');
  }

  public checkDataExported(): void {
    // This would be called when data is exported
    this.completeStep('export_data');
  }
}

export const onboardingManager = OnboardingManager.getInstance();